import type { HwaCollectionSlug } from "./constants";

/**
 * Who can open what in the Healing Works Academy.
 *
 * AIDEV-NOTE: This is the ONE contract every paywall surface reads. It is deliberately a
 * resolved answer ("can this person open this course") rather than raw purchase rows, so
 * the index, the collection page, the upgrade modal and the meditation picker cannot drift
 * into four different interpretations of the same billing state.
 *
 * Two separate things gate a course, and they are not the same question:
 *   - `hwa-launch` (feature flag) — is HWA RELEASED at all, in this environment. When off,
 *     nothing about HWA is visible and none of this is consulted.
 *   - entitlement (below) — has THIS person bought it, and is their access still current.
 */
export interface HwaAccess {
    /** True when the viewer may open resources in this course. */
    unlockedCourses: HwaCollectionSlug[];
    /**
     * They bought at least one course but the recurring access has lapsed.
     *
     * AIDEV-NOTE: Distinct from never having bought. A lapsed buyer has already paid us
     * once and should be asked to resume, never sold from scratch — and must never be told
     * they have no access to something they own. The lapsed UI is NOT designed yet
     * (flagged in the handoff README); until it is, treat lapsed as locked but keep the
     * distinction in the data so the copy can change without a schema change.
     */
    hasLapsedAccess: boolean;
    /** True once every course is unlocked — the shelf then drops all prices and locks. */
    hasFullAcademy: boolean;
    /**
     * When scheduled access ends, ISO-8601. Null while nothing is scheduled.
     *
     * AIDEV-NOTE: The fourth state, and a real one: "cancelled, still open". Stripe cancels
     * at period end, so between the cancel and the date the subscription is genuinely still
     * active — `unlockedCourses` is full, `hasLapsedAccess` is false, and nothing else here
     * can tell that case from an untouched account. That gap is EVO-2485: someone cancelled,
     * the page changed nothing, and they reasonably went back to cancel again.
     *
     * It lives on this contract rather than being read per surface for the stated reason
     * this interface exists — the index, the collection page and the shelf must not drift
     * into three readings of one billing state. Derive the boolean (`accessEndsAt !== null`),
     * never store a second flag beside it.
     *
     * Resolved from the `stripe.*` sync tables by `resolveSubscriptionExpiry`, which is the
     * SAME derivation /account/membership uses for the Parts Companion plan.
     */
    accessEndsAt: string | null;
}

export const EMPTY_HWA_ACCESS: HwaAccess = {
    unlockedCourses: [],
    hasLapsedAccess: false,
    hasFullAcademy: false,
    accessEndsAt: null,
};

export function canOpenHwaCourse(
    access: HwaAccess,
    slug: HwaCollectionSlug,
): boolean {
    return access.unlockedCourses.includes(slug);
}

/** What the upgrade modal is being opened to sell. */
export type HwaOfferSelection = "course" | "bundle";

/**
 * Why the paywall appeared, carried into the modal and into checkout analytics.
 *
 * AIDEV-NOTE: `resource` is the common case — they clicked a specific locked resource, and
 * the modal names it back to them, which is the whole reason the context line exists.
 */
export interface HwaPaywallContext {
    /** The course they are being sold. */
    collectionSlug: HwaCollectionSlug;
    /** Human title of the course, for the context line. */
    collectionTitle: string;
    /** The specific resource they clicked, when there was one. */
    resourceTitle?: string;
    /** Which surface opened this, for checkout attribution. */
    source:
        | "resources-index"
        | "collection-unlock-bar"
        | "resource-card"
        | "meditation-picker";
}
